import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { Banner, Button, Checkbox, ConfirmDialog, CopyField, DataTable, Dialog, EmptyState, PageHeader, SearchInput, Skeleton, StatusBadge, ToneMarker, TonePill, apiFetch, hasPermission, useApi, useApiMutation, useCurrentUser, useT, useToast, type Column } from "@iga/ui";
import type { Account, Page, ResetResult, SdIdentity, SdIdentityDetail, TaskStatus } from "../lib/types";
import { ActionProgress, useTaskStatus } from "../components/ActionProgress";
import { errMsg, firstName, useFmt, useRelTime } from "../lib/hooks";
import { AccessTables } from "./MyAccess";
import { Avatar, LinkButton, LoadError, PersonLine } from "../components/common";

/** Account status for the badge: `locked` wins, `enabled` is the map's `present`. */
const accountStatus = (a: Account) => (a.locked ? "locked" : a.status === "enabled" ? "present" : a.status);

export function ServiceDeskPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const [dq, setDq] = useState(params.get("q") ?? "");
  useEffect(() => { const h = setTimeout(() => { setDq(q.trim()); setParams(q.trim() ? { q: q.trim() } : {}, { replace: true }); }, 300); return () => clearTimeout(h); }, [q, setParams]);
  const res = useApi<Page<SdIdentity>>(["sd-search"], "/api/portal/servicedesk/search", { q: dq, page: 1, pageSize: 25 }, { enabled: dq.length >= 1 });
  const items = res.data?.items ?? [];
  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={t("p.nav.servicedesk")} subtitle={t("p.sd.subtitle")} />
      <SearchInput className="max-w-2xl" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("p.sd.searchPlaceholder")} aria-label={t("p.sd.searchPlaceholder")} autoFocus />
      <LoadError error={res.error} onRetry={() => void res.refetch()} />
      {dq.length === 0 ? <p className="text-fg-3">{t("p.sd.searchHint")}</p>
        : res.isLoading ? <div className="rounded-card border border-line bg-surface p-4"><Skeleton rows={3} /></div>
        : items.length === 0 ? <EmptyState kind="no-results" title={t("p.sd.noMatch")} body={t("p.sd.noMatchBody")} />
        : (
          <div className="rounded-card border border-line bg-surface overflow-hidden">
            <div className="border-b border-line px-4 py-2 text-small text-fg-3">{(res.data?.total ?? items.length) === 1 ? t("p.sd.person") : t("p.sd.people", { count: res.data?.total ?? items.length })}</div>
            <ul className="divide-y divide-line">
              {items.map((i) => (
                <li key={i.id}>
                  <Link to={`/servicedesk/${i.id}`} className="flex min-h-16 items-center gap-3 px-4 py-2.5 text-fg no-underline hover:bg-surface-2">
                    <Avatar name={i.displayName} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">{i.displayName}</span>
                      <span className="block truncate text-small text-fg-3">{[i.type?.displayName, i.orgUnit, i.email, i.externalId].filter(Boolean).join(" · ")}</span>
                    </span>
                    {i.anyLocked && <StatusBadge domain="account" status="locked" audience="portal" context={i.lockedAccounts > 1 ? String(i.lockedAccounts) : undefined} />}
                    {i.status !== "active" && <StatusBadge domain="identity" status={i.status} audience="portal" compact />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
}

export function ServiceDeskIdentityPage() {
  const t = useT();
  const fmt = useFmt();
  const rel = useRelTime();
  const { id } = useParams<{ id: string }>();
  const me = useCurrentUser();
  const toast = useToast();
  const canReset = hasPermission(me?.user.permissions, "passwords:reset");
  const canRequest = hasPermission(me?.user.permissions, "requests:write");
  const res = useApi<SdIdentityDetail>(["sd-identity", id], `/api/portal/servicedesk/identities/${id}`);
  const [verified, setVerified] = useState(false);
  const [resetFor, setResetFor] = useState<Account | null>(null);
  const [mustChange, setMustChange] = useState(true);
  const [unlockToo, setUnlockToo] = useState(true);
  const [result, setResult] = useState<(ResetResult & { system: string; unlocked: boolean; at: string }) | null>(null);
  const [unlockFor, setUnlockFor] = useState<Account | null>(null);
  const [showAccess, setShowAccess] = useState(false);
  const reset = useApiMutation<void, ResetResult>(() => apiFetch<ResetResult>(`/api/portal/servicedesk/identities/${id}/accounts/${resetFor!.id}/reset-password`, { method: "POST", json: { mustChange, unlock: unlockToo && !!resetFor?.locked } }), [["sd-identity"]]);
  const unlock = useApiMutation<void, { taskId: string }>(() => apiFetch(`/api/portal/servicedesk/identities/${id}/accounts/${unlockFor!.id}/unlock`, { method: "POST", json: {} }), [["sd-identity"]]);
  useEffect(() => { setVerified(false); setShowAccess(false); setResult(null); }, [id]);
  // Live state of the reset just made, and of recent critical actions on this person's accounts (2.9).
  const resultTask = useTaskStatus(result ? [result.taskId] : []);
  const recent = useApi<{ items: TaskStatus[] }>(["sd-actions", id], `/api/portal/servicedesk/identities/${id}/actions`, undefined, {
    enabled: canReset,
    refetchInterval: (q) => (((q.state.data as { items: TaskStatus[] } | undefined)?.items ?? []).some((x) => !["confirmed", "failed", "cancelled"].includes(x.stage)) ? 2000 : 30_000),
  });

  if (res.isLoading) return <div className="rounded-card border border-line bg-surface p-5"><Skeleton rows={3} /></div>;
  if (res.error || !res.data) return <><PageHeader portal title={t("p.nav.servicedesk")} breadcrumb={<Link to="/servicedesk">{t("p.nav.servicedesk")}</Link>} /><LoadError error={res.error ?? t("p.sd.notFound")} /></>;
  const d = res.data; const p = d.identity;
  const first = firstName(p.displayName);
  const accounts = [...d.accounts].sort((a, b) => Number(!!b.locked) - Number(!!a.locked) || a.system.displayName.localeCompare(b.system.displayName));
  const title = (p.attributes?.jobTitle as string | undefined) ?? p.typeName;
  const gate = (ok: boolean) => canReset && verified && ok;
  const accountActions = (a: Account) => {
    const showUnlock = !!a.canUnlock && !!a.locked;
    const showReset = !!a.canResetPassword;
    if (!showUnlock && !showReset) return <span className="text-small text-fg-3">{t("p.sd.noActions")}</span>;
    return (
      <div className="flex flex-wrap gap-2 md:justify-end">
        {showUnlock && <Button size="touch" variant="secondary" disabled={!gate(true)} onClick={() => setUnlockFor(a)}>{t("p.sd.unlock")}</Button>}
        {showReset && <Button size="touch" variant="secondary" disabled={!gate(true)} onClick={() => { setResetFor(a); setMustChange(true); setUnlockToo(true); }}>{t("p.sd.reset")}</Button>}
      </div>
    );
  };
  const accountColumns: Column<Account>[] = [
    { key: "account", header: t("p.sd.col.account"), render: (a) => <><div className="font-semibold">{a.system.displayName}</div><div className="text-mono text-fg-2">{a.userName ?? a.nativeId}</div></> },
    { key: "status", header: t("p.sd.col.status"), render: (a) => <div className="flex flex-wrap items-center gap-1.5"><StatusBadge domain="account" status={accountStatus(a)} audience="portal" />{a.locked && a.status === "disabled" && <StatusBadge domain="account" status="disabled" audience="portal" compact />}{a.provisioningState === "pending" && <TonePill tone="info" compact>{t("p.sd.changePending")}</TonePill>}</div> },
    { key: "lastRead", header: t("p.sd.col.lastRead"), render: (a) => <span className="text-fg-2">{a.lastSeenAt ? rel(a.lastSeenAt) : "–"}</span> },
    { key: "actions", header: t("p.sd.col.actions"), align: "right", render: (a) => accountActions(a) },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={p.displayName} breadcrumb={<Link to="/servicedesk">{t("p.nav.servicedesk")}</Link>}
        status={<StatusBadge domain="identity" status={p.status} audience="portal" params={{ date: fmt.day(p.validFrom) }} />}
        subtitle={[title, p.orgUnit?.name, p.email, p.externalId].filter(Boolean).join(" · ")} />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* Phone: confirm the caller first, so the verification card comes before the accounts (§6 J6). */}
        <section className={clsx("rounded-card border bg-surface flex flex-col gap-3 p-4 lg:col-start-2 lg:row-start-1", verified ? "border-line" : "border-brand")}>
          <h2 className="text-h3">{t("p.sd.beforeTitle")}</h2>
          <Checkbox checked={verified} onChange={(e) => setVerified(e.target.checked)} label={t("p.sd.verified")} description={t("p.sd.verifyHint", { id: p.externalId ?? "–", manager: p.manager?.displayName ?? "–" })} />
          {!canReset && <p className="text-small text-fg-3">{t("p.sd.noResetPermission")}</p>}
        </section>
        <div className="flex flex-col gap-5 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <section>
            <h2 className="mb-3 text-h2">{t("p.sd.accounts")}</h2>
            {accounts.length === 0 ? <p className="rounded-card border border-line bg-surface p-4 text-fg-3">{t("p.sd.noAccounts")}</p> : (
              <>
                <ul className="flex flex-col gap-3 md:hidden">
                  {accounts.map((a) => (
                    <li key={a.id} className="rounded-card border border-line bg-surface p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div><div className="font-semibold">{a.system.displayName}</div><div className="text-mono text-fg-2">{a.userName ?? a.nativeId}</div></div>
                        <StatusBadge domain="account" status={accountStatus(a)} audience="portal" />
                      </div>
                      <div className="mt-1 text-small text-fg-3">{t("p.sd.col.lastRead")}: {a.lastSeenAt ? rel(a.lastSeenAt) : "–"}</div>
                      <div className="mt-3">{accountActions(a)}</div>
                    </li>
                  ))}
                </ul>
                <div className="hidden md:block"><DataTable<Account> density="portal" columns={accountColumns} rows={accounts} rowKey={(a) => a.id} /></div>
              </>
            )}
          </section>
          {showAccess && <section><h2 className="mb-3 text-h2">{t("p.sd.accessOf", { name: first })}</h2><AccessTables data={d} /></section>}
          {(recent.data?.items.length ?? 0) > 0 && (
            <section className="rounded-card border border-line bg-surface overflow-hidden">
              <h2 className="border-b border-line px-4 py-3 text-h3">{t("p.sd.recentActions")}</h2>
              <ul className="divide-y divide-line">{recent.data!.items.map((x) => (
                <li key={x.id} className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{t(`p.sd.op.${x.operation}`)}</span>
                    <span className="text-mono text-fg-2">{x.accountName ?? "–"}</span>
                    <span className="ml-auto"><ActionProgress task={x} compact /></span>
                  </div>
                  {x.stage !== "confirmed" && x.stage !== "cancelled" && <ActionProgress task={x} />}
                </li>
              ))}</ul>
            </section>
          )}
          {d.recentRequests.length > 0 && (
            <section className="rounded-card border border-line bg-surface overflow-hidden">
              <h2 className="border-b border-line px-4 py-3 text-h3">{t("p.sd.recentRequests")}</h2>
              <ul className="divide-y divide-line">{d.recentRequests.slice(0, 5).map((r) => (
                <li key={r.id} className="flex min-h-12 flex-wrap items-center gap-2 px-4 py-2">
                  <Link to={`/requests/${r.id}`} className="font-semibold">{(r.items ?? []).map((i) => i.displayName).filter(Boolean).join(", ") || t("p.requests.number", { n: r.number })}</Link>
                  <StatusBadge domain="request" status={r.state} audience="portal" params={{ name: t("p.requests.approval") }} compact />
                  <span className="text-small text-fg-3">{fmt.day(r.createdAt)} · {r.requester.displayName}</span>
                </li>
              ))}</ul>
            </section>
          )}
        </div>
        <section className="rounded-card border border-line bg-surface flex flex-col gap-2 p-4 lg:col-start-2 lg:row-start-2 lg:self-start">
          <h2 className="text-h3">{t("p.sd.otherHelp")}</h2>
          {canRequest && <LinkButton to={`/request?for=${p.id}`} className="justify-start">{t("p.sd.requestFor", { name: first })}</LinkButton>}
          <Button variant="secondary" size="touch" className="justify-start" onClick={() => setShowAccess((v) => !v)} aria-expanded={showAccess}>{showAccess ? t("p.sd.hideAccess", { name: first }) : t("p.sd.seeAccess", { name: first })}</Button>
          <dl className="mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-y-1 text-small text-fg-2">
            <dt className="text-fg-3">{t("p.profile.manager")}</dt><dd>{p.manager?.displayName ?? "–"}</dd>
            <dt className="text-fg-3">{t("p.profile.userName")}</dt><dd className="font-mono">{p.userName ?? "–"}</dd>
            <dt className="text-fg-3">{t("p.sd.portalLogin")}</dt><dd>{d.portalUser ? (d.portalUser.disabled ? t("p.sd.loginDisabled") : d.portalUser.lastLoginAt ? t("p.sd.lastLogin", { when: rel(d.portalUser.lastLoginAt) }) : t("p.sd.neverLoggedIn")) : t("p.sd.noLogin")}</dd>
          </dl>
        </section>
      </div>

      {/* Reset: options */}
      <Dialog open={!!resetFor && !result} onClose={() => setResetFor(null)} title={t("p.sd.resetTitle", { system: resetFor?.system.displayName ?? "" })} size="sm"
        description={t("p.sd.resetBody", { account: resetFor?.userName ?? resetFor?.nativeId ?? "", name: first })}
        footer={<>
          <Button variant="secondary" onClick={() => setResetFor(null)}>{t("ui.cancel")}</Button>
          <Button loading={reset.isPending} onClick={() => reset.mutate(undefined, { onSuccess: (r) => setResult({ ...r, system: resetFor!.system.displayName, unlocked: unlockToo && !!resetFor!.locked, at: new Date().toISOString() }), onError: (e) => toast.push(errMsg(e), { tone: "bad" }) })}>{t("p.sd.resetConfirm")}</Button>
        </>}>
        <div className="flex flex-col gap-3">
          <Checkbox label={t("p.sd.mustChange")} checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
          {resetFor?.locked && resetFor.canUnlock && <Checkbox label={t("p.sd.alsoUnlock")} checked={unlockToo} onChange={(e) => setUnlockToo(e.target.checked)} />}
          <LoadError error={reset.error} title={t("p.sd.resetFailed")} />
        </div>
      </Dialog>

      {/* Reset: the one-time password */}
      <Dialog open={!!result} onClose={() => { setResult(null); setResetFor(null); }} title={t("p.sd.newPasswordFor", { account: result?.account.userName ?? "" })} size="sm"
        description={<span>{result?.system} · {p.displayName}</span>}
        footer={<Button size="lg" onClick={() => { setResult(null); setResetFor(null); toast.push(t("p.sd.handedOver", { name: first }), { tone: "ok" }); }}>{t("p.sd.givenTo", { name: first })}</Button>}>
        {result && (
          <div className="flex flex-col gap-3">
            <p className="sr-only" role="alert">{t("p.sd.shownOnceShort")}</p>
            <CopyField value={result.password} large />
            {resultTask.data?.items[0] ? <ActionProgress task={resultTask.data.items[0]} /> : <p className="text-small text-fg-3">{t("p.sd.stage.queued")}</p>}
            <ul className="flex flex-col gap-1 text-fg-2">
              {result.unlocked && resultTask.data?.items[0]?.stage === "confirmed" && <li className="flex items-center gap-2"><span className="inline-flex text-ok"><ToneMarker tone="ok" /></span>{t("p.sd.unlocked")}</li>}
              <li>{result.mustChange ? t("p.sd.mustChangeNote", { name: first }) : t("p.sd.noChangeNote", { name: first })}</li>
            </ul>
            <Banner tone="warn" title={t("p.sd.shownOnce")} body={t("p.sd.shownOnceBody", { name: first })} />
          </div>
        )}
      </Dialog>

      <ConfirmDialog open={!!unlockFor} onClose={() => setUnlockFor(null)} title={t("p.sd.unlockTitle", { account: unlockFor?.userName ?? unlockFor?.nativeId ?? "" })} confirmLabel={t("p.sd.unlock")} keepLabel={t("p.sd.keepLocked")} loading={unlock.isPending}
        consequence={t("p.sd.unlockBody", { system: unlockFor?.system.displayName ?? "", name: first })}
        onConfirm={() => unlock.mutate(undefined, { onSuccess: () => { toast.push(t("p.sd.unlockQueued", { name: first }), { tone: "ok" }); setUnlockFor(null); }, onError: (e) => { toast.push(errMsg(e), { tone: "bad" }); setUnlockFor(null); } })} />
    </div>
  );
}
